<script>
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';
import { api, sucesso, erro } from '@/helpers/common/api';

export default {
  name: 'ImportacaoContatos',
  components: { Layout, PageHeader },
  props: {
    embutido: { type: Boolean, default: false },
  },
  data() {
    return {
      etapa: 1,
      nomeOrigem: '',
      arquivo: null,
      arquivoNome: '',
      carregando: false,
      origemId: null,
      colunasDetectadas: [],
      dadosImportados: [],
      mapeamentosDetalhados: {},
      errosMapeamento: [],
      camposCustomizados: [],
      camposContato: [],
      jobId: null,
      jobStatus: {
        status: 'pendente',
        progressoPercentual: 0,
        totalLinhas: 0,
        linhasLidas: 0,
        linhasProcessadas: 0,
        linhasRevisao: 0,
        mensagem: '',
        logs: [],
      },
      pollingInterval: null,
      tiposCampoCustom: [
        { value: 'text', label: 'Texto' },
        { value: 'number', label: 'Número' },
        { value: 'date', label: 'Data' },
        { value: 'time', label: 'Hora' },
        { value: 'checkbox', label: 'Checkbox' },
        { value: 'dropdown', label: 'Lista' },
      ],
      transformacoesDisponiveis: [
        { value: 'trim', label: 'Remover espaços' },
        { value: 'digits_only', label: 'Somente dígitos' },
        { value: 'lowercase', label: 'Minúsculas' },
        { value: 'uppercase', label: 'Maiúsculas' },
        { value: 'date_br_to_iso', label: 'Data BR para ISO' },
      ],
      historicoImportacoes: [],
      loadingHistorico: false,
      rotulosDisponiveis: [],
      rotulosSelecionados: [],
      novoRotulo: '',
      historicoDebounceTimer: null,
      filtrosHistorico: {
        busca: '',
        status: '',
      },
      paginacaoHistorico: {
        paginaAtual: 1,
        registrosPorPagina: 10,
        totalRegistros: 0,
      },
    };
  },
  computed: {
    wrapperComponent() {
      return this.embutido ? 'div' : Layout;
    },
    jobFinalizado() {
      return ['concluido', 'concluido_com_alertas', 'falhou'].includes(this.jobStatus.status);
    },
    statusBarClass() {
      const map = {
        pendente: 'bg-secondary',
        processando: 'bg-primary progress-bar-striped progress-bar-animated',
        concluido: 'bg-success',
        concluido_com_alertas: 'bg-warning',
        falhou: 'bg-danger',
      };
      return map[this.jobStatus.status] || 'bg-primary';
    },
    statusBadgeClass() {
      const map = {
        pendente: 'badge bg-secondary-subtle text-secondary',
        processando: 'badge bg-primary-subtle text-primary',
        concluido: 'badge bg-success-subtle text-success',
        concluido_com_alertas: 'badge bg-warning-subtle text-warning',
        falhou: 'badge bg-danger-subtle text-danger',
      };
      return map[this.jobStatus.status] || 'badge bg-secondary-subtle text-secondary';
    },
    totalPaginasHistorico() {
      return Math.ceil(this.paginacaoHistorico.totalRegistros / this.paginacaoHistorico.registrosPorPagina) || 1;
    },
  },
  async created() {
    await Promise.all([this.carregarHistoricoImportacao(), this.carregarRotulos()]);
  },
  beforeUnmount() {
    this.pararPolling();
    if (this.historicoDebounceTimer) clearTimeout(this.historicoDebounceTimer);
  },
  methods: {
    async carregarRotulos() {
      const result = await api.read('/rotulo?limit=9999');
      const lista = result?.data || result || [];
      this.rotulosDisponiveis = Array.isArray(lista) ? lista : [];
    },
    async criarRotulo() {
      const nome = this.novoRotulo.trim();
      if (!nome) return;
      const result = await api.create('/rotulo', { nome, ativo: true });
      if (result) {
        const novo = result.data || result;
        this.rotulosDisponiveis.push(novo);
        this.rotulosSelecionados.push(novo.id);
        this.novoRotulo = '';
        sucesso({ title: `Etiqueta "${nome}" criado!` });
      }
    },
    normalizarChaveTecnica(texto) {
      const base = String(texto || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

      return base || 'campo_custom';
    },

    parseCsvLine(line, delimiter) {
      const result = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i += 1) {
        const char = line[i];
        const next = line[i + 1];

        if (char === '"') {
          if (inQuotes && next === '"') {
            current += '"';
            i += 1;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (char === delimiter && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }

      result.push(current.trim());
      return result;
    },

    detectarDelimitador(headerLine) {
      const candidates = [';', ',', '\t'];
      let best = ';';
      let bestCount = 0;

      candidates.forEach((item) => {
        const count = headerLine.split(item).length;
        if (count > bestCount) {
          best = item;
          bestCount = count;
        }
      });

      return best;
    },

    async lerCsvArquivo(file) {
      const text = await file.text();
      const lines = String(text || '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => !!line);

      if (lines.length < 2) {
        throw new Error('O CSV precisa ter cabeçalho e ao menos uma linha de dados.');
      }

      const delimiter = this.detectarDelimitador(lines[0]);
      const headers = this.parseCsvLine(lines[0], delimiter);

      const rows = lines.slice(1).map((line, index) => {
        const values = this.parseCsvLine(line, delimiter);
        const row = { _linha: index + 2 };

        headers.forEach((header, i) => {
          row[header] = values[i] ?? '';
        });

        return row;
      });

      return { headers, rows };
    },

    getDefaultMapping(coluna) {
      return {
        campoOrigem: coluna,
        destinoTipo: '',
        campoCanonico: '',
        campoPersonalizadoId: null,
        transformacoes: [],
        novoCampo: {
          nomeExibicao: coluna,
          chaveTecnica: this.normalizarChaveTecnica(coluna),
          tipoCampo: 'text',
        },
      };
    },

    defaultTransformsForCanonico(campoCanonico) {
      const map = {
        telefonePrincipal: ['trim', 'digits_only'],
        documento: ['trim', 'digits_only'],
        cep: ['trim', 'digits_only'],
        email: ['trim', 'lowercase'],
        estado: ['trim', 'uppercase'],
        data_nascimento: ['trim', 'date_br_to_iso'],
        nome: ['trim'],
        sobrenome: ['trim'],
        cidade: ['trim'],
        bairro: ['trim'],
        logradouro: ['trim'],
        numero: ['trim'],
        complemento: ['trim'],
      };

      return map[campoCanonico] || ['trim'];
    },

    onDestinoTipoChange(coluna) {
      const map = this.mapeamentosDetalhados[coluna];
      if (!map) return;

      if (map.destinoTipo === 'contato') {
        map.transformacoes = this.defaultTransformsForCanonico(map.campoCanonico);
      } else if (map.destinoTipo === 'custom_existente' || map.destinoTipo === 'custom_novo') {
        map.transformacoes = ['trim'];
      } else {
        map.transformacoes = [];
      }
    },

    onCampoCanonicoChange(coluna) {
      const map = this.mapeamentosDetalhados[coluna];
      if (!map || map.destinoTipo !== 'contato') return;
      map.transformacoes = this.defaultTransformsForCanonico(map.campoCanonico);
    },

    aplicarSugestao(coluna, sugestao) {
      const current = this.getDefaultMapping(coluna);
      if (!sugestao) return current;

      if (sugestao.destinoTipo === 'contato') {
        return {
          ...current,
          destinoTipo: 'contato',
          campoCanonico: sugestao.campoCanonico || '',
          transformacoes: Array.isArray(sugestao.transformacoes)
            ? sugestao.transformacoes
            : this.defaultTransformsForCanonico(sugestao.campoCanonico),
        };
      }

      if (sugestao.destinoTipo === 'custom_existente') {
        return {
          ...current,
          destinoTipo: 'custom_existente',
          campoPersonalizadoId: Number(sugestao.campoPersonalizadoId || 0) || null,
          transformacoes: Array.isArray(sugestao.transformacoes) ? sugestao.transformacoes : ['trim'],
        };
      }

      return current;
    },

    selecionarArquivo(event) {
      const file = event.target.files[0] || null;
      this.arquivo = file;
      this.arquivoNome = file?.name || '';
    },

    async gerarPreview() {
      if (!this.arquivo) return;

      this.carregando = true;
      this.errosMapeamento = [];

      let parsed;
      try {
        parsed = await this.lerCsvArquivo(this.arquivo);
      } catch (e) {
        this.carregando = false;
        erro({ title: 'Falha ao ler CSV', text: e.message });
        return;
      }

      this.colunasDetectadas = parsed.headers;
      this.dadosImportados = parsed.rows;

      const result = await api.create('/importacao/preview', {
        nomeOrigem: this.nomeOrigem,
        arquivoNome: this.arquivoNome || 'upload.csv',
        colunasDetectadas: parsed.headers,
        linhasAmostra: parsed.rows.slice(0, 20),
      });

      this.carregando = false;
      if (!result) {
        erro({ title: 'Erro ao analisar arquivo' });
        return;
      }

      this.origemId = result.origemId;
      this.colunasDetectadas = result.colunasDetectadas || [];
      this.camposCustomizados = result.camposCustomizados || [];
      this.camposContato = result.camposContato || [];

      const base = {};
      this.colunasDetectadas.forEach((coluna) => {
        base[coluna] = this.getDefaultMapping(coluna);
      });

      const prefill = result.mapeamentosSalvos || result.sugestoesMapeamento || {};
      Object.keys(prefill || {}).forEach((coluna) => {
        if (base[coluna]) {
          base[coluna] = this.aplicarSugestao(coluna, prefill[coluna]);
        }
      });

      Object.keys(result.sugestoesMapeamento || {}).forEach((coluna) => {
        if (base[coluna] && !base[coluna].destinoTipo) {
          base[coluna] = this.aplicarSugestao(coluna, result.sugestoesMapeamento[coluna]);
        }
      });

      this.mapeamentosDetalhados = base;
      this.etapa = 2;
    },

    validarMapeamento() {
      this.errosMapeamento = [];
      const mapeamentos = Object.values(this.mapeamentosDetalhados).filter((map) => !!map.destinoTipo);

      mapeamentos.forEach((map) => {
        if (map.destinoTipo === 'contato' && !map.campoCanonico) {
          this.errosMapeamento.push(`Defina o campo de destino para a coluna ${map.campoOrigem}.`);
        }

        if (map.destinoTipo === 'custom_existente' && !map.campoPersonalizadoId) {
          this.errosMapeamento.push(`Selecione o campo personalizado existente para a coluna ${map.campoOrigem}.`);
        }

        if (map.destinoTipo === 'custom_novo') {
          const nome = String(map.novoCampo?.nomeExibicao || '').trim();
          const chave = String(map.novoCampo?.chaveTecnica || '').trim();
          if (!nome || !chave) {
            this.errosMapeamento.push(`Preencha nome e chave técnica do novo campo na coluna ${map.campoOrigem}.`);
          }
        }
      });

      return this.errosMapeamento.length === 0;
    },

    possuiTelefoneUtilizavel(valor) {
      const digits = String(valor ?? '').replace(/\D/g, '');
      return digits.length > 0;
    },

    validarDadosMinimosParaProcessar() {
      const telefoneMap = Object.values(this.mapeamentosDetalhados).find(
        (map) => map.destinoTipo === 'contato' && map.campoCanonico === 'telefonePrincipal'
      );

      if (!telefoneMap) return true;

      const colunaTelefone = telefoneMap.campoOrigem;
      const total = this.dadosImportados.length;
      const comTelefone = this.dadosImportados.filter((row) => this.possuiTelefoneUtilizavel(row[colunaTelefone])).length;

      if (comTelefone === 0) {
        this.errosMapeamento = [
          `Nenhum telefone foi identificado na coluna "${colunaTelefone}". A importação seguirá usando e-mail/documento quando disponíveis.`,
        ];
        return true;
      }

      if (comTelefone < total) {
        this.errosMapeamento = [
          `Apenas ${comTelefone} de ${total} linhas possuem telefone na coluna "${colunaTelefone}". Linhas sem telefone irão para revisão.`,
        ];
      }

      return true;
    },

    async iniciarProcessamento() {
      if (!this.validarMapeamento()) return;
      if (!this.validarDadosMinimosParaProcessar()) return;

      this.carregando = true;
      const mapeamentosArray = Object.values(this.mapeamentosDetalhados)
        .filter((item) => !!item.destinoTipo)
        .map((item) => ({
          campoOrigem: item.campoOrigem,
          destinoTipo: item.destinoTipo,
          campoCanonico: item.campoCanonico || null,
          campoPersonalizadoId: item.campoPersonalizadoId || null,
          transformacoes: Array.isArray(item.transformacoes) ? item.transformacoes : [],
          novoCampo: item.destinoTipo === 'custom_novo'
            ? {
                nomeExibicao: String(item.novoCampo.nomeExibicao || '').trim(),
                chaveTecnica: this.normalizarChaveTecnica(item.novoCampo.chaveTecnica || item.novoCampo.nomeExibicao),
                tipoCampo: item.novoCampo.tipoCampo || 'text',
              }
            : null,
        }));

      const result = await api.create('/importacao/processar', {
        origemId: this.origemId,
        mapeamentos: mapeamentosArray,
        dadosImportados: this.dadosImportados,
        rotuloIds: this.rotulosSelecionados,
      });

      this.carregando = false;
      if (!result) {
        erro({ title: 'Erro ao iniciar importação' });
        return;
      }

      this.jobId = result.jobId;
      this.etapa = 3;
      this.iniciarPolling();
    },

    iniciarPolling() {
      this.pollingInterval = setInterval(async () => {
        const result = await api.read(`/importacao/job?jobId=${this.jobId}`);
        if (result) {
          this.jobStatus = result;
          if (this.jobFinalizado) {
            this.pararPolling();
            if (this.jobStatus.status === 'concluido') {
              sucesso({ title: 'Importação concluída com sucesso!' });
            } else if (this.jobStatus.status === 'concluido_com_alertas') {
              sucesso({ title: 'Importação concluída com alertas', icon: 'warning' });
            }
          }
        }
      }, 2000);
    },

    pararPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    novaImportacao() {
      this.etapa = 1;
      this.nomeOrigem = '';
      this.arquivo = null;
      this.arquivoNome = '';
      this.origemId = null;
      this.colunasDetectadas = [];
      this.dadosImportados = [];
      this.mapeamentosDetalhados = {};
      this.errosMapeamento = [];
      this.camposCustomizados = [];
      this.camposContato = [];
      this.rotulosSelecionados = [];
      this.novoRotulo = '';
      this.jobId = null;
      this.jobStatus = {
        status: 'pendente',
        progressoPercentual: 0,
        totalLinhas: 0,
        linhasLidas: 0,
        linhasProcessadas: 0,
        linhasRevisao: 0,
        mensagem: '',
        logs: [],
      };
    },

    resumoRegistro(registro) {
      if (!registro) return '—';
      return Object.entries(registro)
        .slice(0, 5)
        .map(([k, v]) => `${k}: ${v || ''}`)
        .join(' | ');
    },

    formatarDataHora(valor) {
      if (!valor) return '—';
      const dt = new Date(valor);
      if (Number.isNaN(dt.getTime())) return '—';
      return dt.toLocaleString('pt-BR');
    },

    statusHistoricoBadge(status) {
      const map = {
        pendente: 'badge bg-secondary-subtle text-secondary',
        processando: 'badge bg-primary-subtle text-primary',
        concluido: 'badge bg-success-subtle text-success',
        concluido_com_alertas: 'badge bg-warning-subtle text-warning',
        falhou: 'badge bg-danger-subtle text-danger',
      };
      return map[status] || 'badge bg-secondary-subtle text-secondary';
    },

    async carregarHistoricoImportacao() {
      this.loadingHistorico = true;
      try {
        const params = new URLSearchParams();
        if (this.filtrosHistorico.busca) params.set('busca', this.filtrosHistorico.busca);
        if (this.filtrosHistorico.status) params.set('status', this.filtrosHistorico.status);

        const offset = (this.paginacaoHistorico.paginaAtual - 1) * this.paginacaoHistorico.registrosPorPagina;
        params.set('offset', String(offset));
        params.set('limit', String(this.paginacaoHistorico.registrosPorPagina));
        params.set('order', JSON.stringify([['id', 'desc']]));

        const [lista, total] = await Promise.all([
          api.read(`/origem/importacao?${params.toString()}`),
          api.read(`/origem/importacao/total?${params.toString()}`),
        ]);

        this.historicoImportacoes = Array.isArray(lista) ? lista : [];
        this.paginacaoHistorico.totalRegistros = Number(total || 0);
      } finally {
        this.loadingHistorico = false;
      }
    },

    debounceHistoricoFiltros() {
      if (this.historicoDebounceTimer) clearTimeout(this.historicoDebounceTimer);
      this.paginacaoHistorico.paginaAtual = 1;
      this.historicoDebounceTimer = setTimeout(() => {
        this.carregarHistoricoImportacao();
      }, 800);
    },

    irParaPaginaHistorico(pagina) {
      if (pagina < 1 || pagina > this.totalPaginasHistorico) return;
      this.paginacaoHistorico.paginaAtual = pagina;
      this.carregarHistoricoImportacao();
    },

    mudarRegistrosPorPaginaHistorico(novoLimite) {
      this.paginacaoHistorico.registrosPorPagina = Number(novoLimite);
      this.paginacaoHistorico.paginaAtual = 1;
      this.carregarHistoricoImportacao();
    },

    async reprocessar(item) {
      item._reprocessando = true;
      try {
        const result = await api.create(`/importacao/reprocessar`, { origemId: item.id });
        if (result && result.jobId) {
          this.jobId = result.jobId;
          this.jobStatus = {
            status: 'pendente',
            progressoPercentual: 0,
            totalLinhas: item.total_linhas || 0,
            linhasLidas: 0,
            linhasProcessadas: 0,
            linhasRevisao: 0,
            mensagem: '',
            logs: [],
          };
          this.etapa = 3;
          this.iniciarPolling();
          sucesso({ title: 'Reprocessamento iniciado' });
        } else {
          erro({ title: 'Não foi possível reprocessar', text: 'Dados da importação original não encontrados.' });
        }
      } catch (_error) {
        erro({ title: 'Erro ao reprocessar importação' });
      } finally {
        item._reprocessando = false;
      }
    },
  },
};
</script>

<template>
  <component :is="wrapperComponent">
    <PageHeader v-if="!embutido" title="Importação de Contatos" pageTitle="CRM" />

    <BRow>
      <BCol lg="12">
        <BCard no-body>
          <!-- Steps indicator -->
          <div class="card-header border-0">
            <div class="d-flex align-items-center">
              <div class="flex-grow-1">
                <h5 class="card-title mb-0">
                  <span class="badge bg-primary-subtle text-primary rounded-pill me-2">{{ etapa }}/3</span>
                  {{ etapa === 1 ? 'Upload do Arquivo' : etapa === 2 ? 'Mapeamento de Campos' : 'Processamento' }}
                </h5>
              </div>
              <div class="flex-shrink-0">
                <span :class="statusBadgeClass">{{ jobStatus.status }}</span>
              </div>
            </div>
          </div>
          <BCardBody>
            <!-- Etapa 1: Upload e Preview -->
            <div v-if="etapa === 1">
              <BRow class="mb-3">
                <BCol md="6">
                  <label class="form-label">Nome da Origem</label>
                  <input v-model="nomeOrigem" type="text" class="form-control" placeholder="Ex: Base Marketing Maio" />
                </BCol>
                <BCol md="6">
                  <label class="form-label">Arquivo (CSV)</label>
                  <input type="file" class="form-control" accept=".csv" @change="selecionarArquivo" />
                </BCol>
              </BRow>
              <BButton variant="primary" :disabled="!nomeOrigem || !arquivo || carregando" @click="gerarPreview">
                <BSpinner v-if="carregando" small class="me-1" />
                <i v-else class="ri-file-search-line align-bottom me-1"></i>
                {{ carregando ? 'Analisando...' : 'Analisar Arquivo' }}
              </BButton>
            </div>

            <!-- Etapa 2: Mapeamento -->
            <div v-if="etapa === 2">
              <p class="text-muted mb-3">Associe cada coluna do arquivo a um campo do CRM.</p>

              <div class="table-responsive">
                <table class="table table-borderless align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Coluna do Arquivo</th>
                      <th>Tipo de Destino</th>
                      <th>Campo CRM</th>
                      <th>Transformações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="col in colunasDetectadas" :key="col">
                      <td>
                        <span class="fw-medium">{{ col }}</span>
                      </td>
                      <td>
                        <Multiselect
                          v-model="mapeamentosDetalhados[col].destinoTipo"
                          :options="[
                            { value: '', label: '— Ignorar —' },
                            { value: 'contato', label: 'Campo do contato' },
                            { value: 'custom_existente', label: 'Campo personalizado existente' },
                            { value: 'custom_novo', label: 'Criar novo campo personalizado' },
                          ]"
                          label="label"
                          value-prop="value"
                          :searchable="true"
                          :can-clear="false"
                          :can-deselect="false"
                          @change="onDestinoTipoChange(col)"
                        />
                      </td>
                      <td>
                        <div v-if="mapeamentosDetalhados[col].destinoTipo === 'contato'">
                          <Multiselect
                            v-model="mapeamentosDetalhados[col].campoCanonico"
                            :options="camposContato"
                            label="label"
                            value-prop="value"
                            :searchable="true"
                            :can-clear="false"
                            :can-deselect="false"
                            @change="onCampoCanonicoChange(col)"
                          />
                        </div>

                        <div v-else-if="mapeamentosDetalhados[col].destinoTipo === 'custom_existente'">
                          <Multiselect
                            v-model="mapeamentosDetalhados[col].campoPersonalizadoId"
                            :options="camposCustomizados.map((item) => ({ value: item.id, label: `${item.nome_exibicao} (${item.chave_tecnica})` }))"
                            label="label"
                            value-prop="value"
                            :searchable="true"
                            :can-clear="false"
                            :can-deselect="false"
                          />
                        </div>

                        <div v-else-if="mapeamentosDetalhados[col].destinoTipo === 'custom_novo'">
                          <BRow class="g-2">
                            <BCol md="5">
                              <input
                                v-model="mapeamentosDetalhados[col].novoCampo.nomeExibicao"
                                type="text"
                                class="form-control"
                                placeholder="Nome de exibição"
                              />
                            </BCol>
                            <BCol md="4">
                              <input
                                v-model="mapeamentosDetalhados[col].novoCampo.chaveTecnica"
                                type="text"
                                class="form-control"
                                placeholder="chave_tecnica"
                              />
                            </BCol>
                            <BCol md="3">
                              <Multiselect
                                v-model="mapeamentosDetalhados[col].novoCampo.tipoCampo"
                                :options="tiposCampoCustom"
                                label="label"
                                value-prop="value"
                                :searchable="false"
                                :can-clear="false"
                                :can-deselect="false"
                              />
                            </BCol>
                          </BRow>
                        </div>

                        <div v-else class="text-muted small">Sem mapeamento</div>
                      </td>
                      <td>
                        <Multiselect
                          v-model="mapeamentosDetalhados[col].transformacoes"
                          :options="transformacoesDisponiveis"
                          mode="tags"
                          label="label"
                          value-prop="value"
                          :searchable="true"
                          :close-on-select="false"
                          :can-clear="true"
                          :can-deselect="true"
                          :disabled="!mapeamentosDetalhados[col].destinoTipo"
                          placeholder="Sem transformações"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="errosMapeamento.length" class="alert alert-border-left alert-warning mt-3">
                <i class="ri-alert-line me-2 align-middle"></i>
                <strong>Atenção:</strong>
                <ul class="mb-0 mt-1">
                  <li v-for="err in errosMapeamento" :key="err">{{ err }}</li>
                </ul>
              </div>

              <!-- Etiquetas para vincular à importação -->
              <div class="card border mt-3">
                <div class="card-body py-3">
                  <h6 class="mb-2">
                    <i class="ri-price-tag-3-line me-1 align-bottom"></i>
                    Vincular etiquetas aos contatos importados
                    <small class="text-muted fw-normal">(opcional)</small>
                  </h6>
                  <BRow class="g-2 align-items-end">
                    <BCol cols="12" md="8">
                      <Multiselect
                        v-model="rotulosSelecionados"
                        :options="rotulosDisponiveis"
                        mode="tags"
                        label="nome"
                        value-prop="id"
                        :searchable="true"
                        :close-on-select="false"
                        :create-option="false"
                        placeholder="Selecione etiquetas existentes..."
                        no-results-text="Nenhuma etiqueta encontrado"
                      />
                    </BCol>
                    <BCol cols="12" md="4">
                      <div class="input-group">
                        <input
                          v-model="novoRotulo"
                          type="text"
                          class="form-control"
                          placeholder="Criar nova etiqueta..."
                          @keyup.enter="criarRotulo"
                        />
                        <BButton variant="soft-success" :disabled="!novoRotulo.trim()" @click="criarRotulo">
                          <i class="ri-add-line"></i>
                        </BButton>
                      </div>
                    </BCol>
                  </BRow>
                </div>
              </div>

              <div class="hstack gap-2 mt-3">
                <BButton variant="ghost-dark" @click="etapa = 1">
                  <i class="ri-arrow-left-line align-bottom me-1"></i> Voltar
                </BButton>
                <BButton variant="success" :disabled="carregando" @click="iniciarProcessamento">
                  <BSpinner v-if="carregando" small class="me-1" />
                  <i v-else class="ri-upload-2-line align-bottom me-1"></i>
                  {{ carregando ? 'Processando...' : 'Iniciar Importação' }}
                </BButton>
              </div>
            </div>

            <!-- Etapa 3: Acompanhamento -->
            <div v-if="etapa === 3">
              <div class="mb-4">
                <div class="progress progress-xl" style="height: 25px">
                  <div
                    class="progress-bar"
                    :class="statusBarClass"
                    :style="{ width: jobStatus.progressoPercentual + '%' }"
                    role="progressbar"
                  >
                    {{ jobStatus.progressoPercentual }}%
                  </div>
                </div>
              </div>

              <BRow class="text-center mb-4">
                <BCol md="3">
                  <BCard no-body class="border">
                    <BCardBody class="py-3">
                      <h6 class="text-muted mb-1">Total Linhas</h6>
                      <h4 class="mb-0">{{ jobStatus.totalLinhas }}</h4>
                    </BCardBody>
                  </BCard>
                </BCol>
                <BCol md="3">
                  <BCard no-body class="border">
                    <BCardBody class="py-3">
                      <h6 class="text-muted mb-1">Lidas</h6>
                      <h4 class="mb-0 text-primary">{{ jobStatus.linhasLidas || 0 }}</h4>
                    </BCardBody>
                  </BCard>
                </BCol>
                <BCol md="3">
                  <BCard no-body class="border">
                    <BCardBody class="py-3">
                      <h6 class="text-muted mb-1">Processadas</h6>
                      <h4 class="mb-0 text-success">{{ jobStatus.linhasProcessadas }}</h4>
                    </BCardBody>
                  </BCard>
                </BCol>
                <BCol md="3">
                  <BCard no-body class="border">
                    <BCardBody class="py-3">
                      <h6 class="text-muted mb-1">Em Revisão</h6>
                      <h4 class="mb-0 text-warning">{{ jobStatus.linhasRevisao }}</h4>
                    </BCardBody>
                  </BCard>
                </BCol>
              </BRow>

              <BRow class="text-center mb-4">
                <BCol md="12">
                  <BCard no-body class="border">
                    <BCardBody class="py-3">
                      <h6 class="text-muted mb-1">Status</h6>
                      <span :class="statusBadgeClass">{{ jobStatus.status }}</span>
                    </BCardBody>
                  </BCard>
                </BCol>
              </BRow>

              <p v-if="jobStatus.mensagem && jobStatus.status === 'falhou'" class="alert alert-danger mt-3">
                <i class="ri-error-warning-line me-2 align-middle fs-16"></i>
                <strong>Motivo da falha:</strong> {{ jobStatus.mensagem }}
              </p>
              <p v-else-if="jobStatus.mensagem" class="text-muted">{{ jobStatus.mensagem }}</p>

              <div class="mt-3">
                <h6 class="mb-2">Logs da importação</h6>
                <div class="border rounded p-2 bg-light" style="max-height: 220px; overflow-y: auto;">
                  <div v-if="!(jobStatus.logs || []).length" class="text-muted small">
                    Aguardando logs...
                  </div>
                  <div
                    v-for="(linha, idx) in (jobStatus.logs || [])"
                    :key="`${idx}-${linha}`"
                    class="small py-1 border-bottom"
                  >
                    {{ linha }}
                  </div>
                </div>
              </div>

              <BButton v-if="jobFinalizado" variant="soft-primary" @click="novaImportacao">
                <i class="ri-restart-line align-bottom me-1"></i> Nova Importação
              </BButton>

              <!-- Detalhes de Revisão -->
              <div v-if="jobFinalizado && (jobStatus.revisaoDetalhes || []).length" class="mt-4">
                <h6 class="mb-2 text-warning">
                  <i class="ri-error-warning-line me-1"></i>
                  Registros em revisão ({{ jobStatus.linhasRevisao }})
                </h6>
                <div class="table-responsive border rounded" style="max-height: 300px; overflow-y: auto;">
                  <table class="table table-sm table-striped mb-0 align-middle">
                    <thead class="table-light sticky-top">
                      <tr>
                        <th style="width: 30%">Motivo</th>
                        <th>Dados do Registro</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, idx) in jobStatus.revisaoDetalhes" :key="'rev-'+idx">
                        <td><span class="badge bg-warning-subtle text-warning">{{ item.motivo }}</span></td>
                        <td class="small text-muted text-truncate" style="max-width: 500px">{{ resumoRegistro(item.registro) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <small v-if="jobStatus.linhasRevisao > 200" class="text-muted mt-1 d-block">
                  Exibindo 200 de {{ jobStatus.linhasRevisao }} registros.
                </small>
              </div>

              <!-- Detalhes de Erros -->
              <div v-if="jobFinalizado && (jobStatus.errosDetalhes || []).length" class="mt-4">
                <h6 class="mb-2 text-danger">
                  <i class="ri-close-circle-line me-1"></i>
                  Erros ({{ jobStatus.totalErros }})
                </h6>
                <div class="table-responsive border rounded" style="max-height: 300px; overflow-y: auto;">
                  <table class="table table-sm table-striped mb-0 align-middle">
                    <thead class="table-light sticky-top">
                      <tr>
                        <th style="width: 40%">Erro</th>
                        <th>Dados do Registro</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, idx) in jobStatus.errosDetalhes" :key="'err-'+idx">
                        <td><span class="badge bg-danger-subtle text-danger">{{ item.erro }}</span></td>
                        <td class="small text-muted text-truncate" style="max-width: 500px">{{ resumoRegistro(item.registro) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <small v-if="jobStatus.totalErros > 200" class="text-muted mt-1 d-block">
                  Exibindo 200 de {{ jobStatus.totalErros }} erros.
                </small>
              </div>
            </div>
          </BCardBody>
        </BCard>
      </BCol>
    </BRow>

    <BRow class="mt-3">
      <BCol lg="12">
        <BCard no-body>
          <div class="card-header border-0">
            <h5 class="card-title mb-0">Histórico de Importação</h5>
          </div>
          <BCardBody>
            <BRow class="g-3 mb-3 align-items-center">
              <BCol sm="4">
                <input
                  v-model="filtrosHistorico.busca"
                  type="text"
                  class="form-control"
                  placeholder="Buscar por origem, tipo de arquivo ou responsável..."
                  @input="debounceHistoricoFiltros"
                />
              </BCol>
              <BCol sm="3">
                <Multiselect
                  v-model="filtrosHistorico.status"
                  :options="[
                    { value: '', label: 'Todos os status' },
                    { value: 'pendente', label: 'Pendente' },
                    { value: 'processando', label: 'Processando' },
                    { value: 'concluido', label: 'Concluído' },
                    { value: 'concluido_com_alertas', label: 'Concluído com alertas' },
                    { value: 'falhou', label: 'Falhou' },
                  ]"
                  label="label"
                  value-prop="value"
                  :searchable="true"
                  :can-clear="false"
                  :can-deselect="false"
                  @change="debounceHistoricoFiltros"
                />
              </BCol>
              <BCol sm="3">
                <select
                  v-model.number="paginacaoHistorico.registrosPorPagina"
                  class="form-select"
                  @change="mudarRegistrosPorPaginaHistorico(paginacaoHistorico.registrosPorPagina)"
                >
                  <option :value="10">10 por página</option>
                  <option :value="25">25 por página</option>
                  <option :value="50">50 por página</option>
                </select>
              </BCol>
            </BRow>

            <div v-if="loadingHistorico" class="text-center py-4">
              <BSpinner variant="primary" />
            </div>

            <div v-else class="table-responsive">
              <table class="table table-borderless align-middle table-nowrap mb-0">
                <thead class="table-light">
                  <tr>
                    <th>#</th>
                    <th>Origem</th>
                    <th>Status</th>
                    <th>Total Linhas</th>
                    <th>Válidas</th>
                    <th>Revisão</th>
                    <th>Iniciado em</th>
                    <th>Finalizado em</th>
                    <th>Responsável</th>
                    <th class="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in historicoImportacoes" :key="item.id">
                    <td>{{ item.id }}</td>
                    <td>{{ item.nome_origem || '—' }}</td>
                    <td>
                      <span :class="statusHistoricoBadge(item.status_processamento)" :title="item.erro_detalhado || ''">{{ item.status_processamento || '—' }}</span>
                      <div v-if="item.erro_detalhado && item.status_processamento === 'falhou'" class="text-danger small mt-1" style="max-width: 300px; white-space: normal;">
                        {{ item.erro_detalhado }}
                      </div>
                    </td>
                    <td>{{ item.total_linhas ?? 0 }}</td>
                    <td>{{ item.total_validas ?? 0 }}</td>
                    <td>{{ item.total_revisao ?? 0 }}</td>
                    <td>{{ formatarDataHora(item.iniciado_em) }}</td>
                    <td>{{ formatarDataHora(item.finalizado_em) }}</td>
                    <td>{{ item.usuario_responsavel || '—' }}</td>
                    <td class="text-center">
                      <button
                        v-if="['falhou', 'concluido_com_alertas'].includes(item.status_processamento)"
                        class="btn btn-sm btn-soft-warning"
                        title="Reprocessar importação"
                        :disabled="item._reprocessando"
                        @click="reprocessar(item)"
                      >
                        <BSpinner v-if="item._reprocessando" small />
                        <i v-else class="ri-restart-line"></i>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!historicoImportacoes.length">
                    <td colspan="10" class="text-center text-muted py-4">Nenhuma importação encontrada.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <BRow class="mt-3 align-items-center" v-if="!loadingHistorico">
              <BCol md="6" class="d-flex align-items-center">
                <small class="text-muted">
                  Mostrando
                  <strong>{{ historicoImportacoes.length ? (paginacaoHistorico.paginaAtual - 1) * paginacaoHistorico.registrosPorPagina + 1 : 0 }}</strong>
                  a
                  <strong>{{ Math.min(paginacaoHistorico.paginaAtual * paginacaoHistorico.registrosPorPagina, paginacaoHistorico.totalRegistros) }}</strong>
                  de
                  <strong>{{ paginacaoHistorico.totalRegistros }}</strong>
                  registros
                </small>
              </BCol>
              <BCol md="6" class="text-end">
                <BPagination
                  v-model="paginacaoHistorico.paginaAtual"
                  :total-rows="paginacaoHistorico.totalRegistros"
                  :per-page="paginacaoHistorico.registrosPorPagina"
                  :limit="5"
                  align="end"
                  size="sm"
                  @update:modelValue="irParaPaginaHistorico"
                />
              </BCol>
            </BRow>
          </BCardBody>
        </BCard>
      </BCol>
    </BRow>
  </component>
</template>
